import { useCallback, useEffect, useState } from "react";
import { CalendarClock, CalendarDays, Check, Copy, Eye, Loader2, Pencil, Play, Save, Trash2 } from "lucide-react";
import { LookPreview } from "./LookPreview";
import { LookCalendar } from "./LookCalendar";
import { toast } from "sonner";
import api from "@/lib/api";
import { HUD } from "./theme";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const abs = (u) => (u?.startsWith("/api/") ? `${BACKEND}${u}` : u);
const err = (e, m) => toast.error(e?.response?.data?.detail || m);
const fmt = (d) => new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" });

const Schedule = ({ look, onDone }) => {
  const [s, setS] = useState({ start: look.start, end: look.end, yearly: look.yearly });
  const put = (body) => api.patch(`/admin/hero/looks/${look.id}`, body).then(() => { toast.success(body.start ? "Schedule saved" : "Schedule removed"); onDone(); }).catch((e) => err(e, "Couldn't save schedule"));
  const inp = `h-8 rounded-lg px-2 text-xs border ${HUD.input}`;
  return (
    <div className="space-y-2 p-2 border-t border-cyan-400/10" data-testid={`look-schedule-${look.id}`}>
      <div className="flex gap-1.5">
        <input type="date" value={s.start} onChange={(e) => setS({ ...s, start: e.target.value })} className={inp} data-testid={`look-start-${look.id}`} />
        <input type="date" value={s.end} onChange={(e) => setS({ ...s, end: e.target.value })} className={inp} data-testid={`look-end-${look.id}`} />
      </div>
      <label className="flex items-center gap-2 text-[11px] text-gray-400">
        <input type="checkbox" checked={s.yearly} onChange={(e) => setS({ ...s, yearly: e.target.checked })} data-testid={`look-yearly-${look.id}`} />Repeat every year
      </label>
      <div className="flex gap-1.5">
        <button onClick={() => put(s)} disabled={!s.start || !s.end} className={`${HUD.btn} h-7 px-3 text-[11px] disabled:opacity-40`} data-testid={`look-schedule-save-${look.id}`}>Save dates</button>
        {look.start && <button onClick={() => put({ start: "", end: "" })} className={`${HUD.ghost} h-7 px-3 text-[11px]`} data-testid={`look-schedule-clear-${look.id}`}>Remove</button>}
      </div>
    </div>
  );
};

const LookCard = ({ look, busy, onApply, onUpdate, onReload, onPreview, onDuplicate }) => {
  const [edit, setEdit] = useState(false);
  const [name, setName] = useState(look.name);
  const [sched, setSched] = useState(false);
  const rename = () => api.patch(`/admin/hero/looks/${look.id}`, { name }).then(() => { setEdit(false); onReload(); }).catch((e) => err(e, "Couldn't rename"));
  const remove = () => window.confirm(`Delete the look "${look.name}"?`) && api.delete(`/admin/hero/looks/${look.id}`).then(() => { toast.success("Look deleted"); onReload(); }).catch((e) => err(e, "Couldn't delete"));
  const btn = "w-7 h-7 rounded-lg bg-black/70 flex items-center justify-center text-white hover:text-cyan-300";
  return (
    <div className={`w-56 rounded-xl overflow-hidden border bg-black/40 ${look.live ? "border-cyan-300" : "border-white/10"}`} data-testid={`look-card-${look.id}`}>
      <div className="relative aspect-video bg-black">
        {look.video ? <video src={abs(look.video)} muted playsInline className="w-full h-full object-cover" /> : look.thumb && <img src={abs(look.thumb)} alt={look.name} className="w-full h-full object-cover" />}
        <p className="absolute left-2 bottom-1.5 text-[10px] text-white/90 font-semibold uppercase tracking-wider drop-shadow">{look.line1} {look.line2}</p>
        {look.live && <span className="absolute left-2 top-2 text-[10px] px-2 py-0.5 rounded-full bg-cyan-300 text-black font-semibold flex items-center gap-1" data-testid={`look-live-${look.id}`}><Check className="w-3 h-3" />Live</span>}
        <div className="absolute top-1.5 right-1.5 flex gap-1">
          <button onClick={() => onPreview(look)} className={btn} title="Preview full screen" data-testid={`look-preview-btn-${look.id}`}><Eye className="w-3.5 h-3.5" /></button>
          <button onClick={() => onDuplicate(look)} className={btn} title="Make a copy" data-testid={`look-duplicate-${look.id}`}><Copy className="w-3.5 h-3.5" /></button>
          <button onClick={() => setSched(!sched)} className={btn} title="Schedule dates" data-testid={`look-schedule-btn-${look.id}`}><CalendarClock className="w-3.5 h-3.5" /></button>
          <button onClick={() => setEdit(!edit)} className={btn} title="Rename" data-testid={`look-rename-btn-${look.id}`}><Pencil className="w-3.5 h-3.5" /></button>
          <button onClick={remove} className={`${btn} text-red-300`} title="Delete" data-testid={`look-delete-${look.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      </div>
      <div className="p-2 space-y-1.5">
        {edit ? (
          <div className="flex gap-1.5">
            <input value={name} maxLength={40} onChange={(e) => setName(e.target.value)} className={`flex-1 min-w-0 h-7 rounded-lg px-2 text-xs border ${HUD.input}`} data-testid={`look-rename-input-${look.id}`} />
            <button onClick={rename} className={`${HUD.btn} h-7 px-2 text-[11px]`} data-testid={`look-rename-save-${look.id}`}>OK</button>
          </div>
        ) : <p className="text-sm text-white truncate" data-testid={`look-name-${look.id}`}>{look.name}</p>}
        {look.start && <p className="text-[10px] text-amber-300" data-testid={`look-dates-${look.id}`}>Auto: {fmt(look.start)} – {fmt(look.end)}{look.yearly ? " · every year" : ""}</p>}
        {look.live ? (
          <button onClick={() => onUpdate(look)} disabled={busy} className={`${HUD.btn} w-full h-8 text-xs flex items-center justify-center gap-1.5 disabled:opacity-40`} data-testid={`look-update-${look.id}`}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}Save my changes to this look
          </button>
        ) : (
          <div className="flex gap-1.5">
            <button onClick={() => onApply(look)} disabled={busy} className={`${HUD.ghost} flex-1 h-8 text-xs flex items-center justify-center gap-1.5 disabled:opacity-40`} data-testid={`look-apply-${look.id}`}>
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}Use this look
            </button>
            <button onClick={() => onApply(look, true)} disabled={busy} className={`${HUD.ghost} h-8 px-3 text-xs flex items-center gap-1.5 disabled:opacity-40`} data-testid={`look-edit-${look.id}`}><Pencil className="w-3.5 h-3.5" />Edit</button>
          </div>
        )}
        {look.live && <p className="text-[10px] text-gray-500">This look is live — change anything in the editor below, then press this to update it.</p>}
      </div>
      {sched && <Schedule look={look} onDone={() => { setSched(false); onReload(); }} />}
    </div>
  );
};

// Snapshot the whole homepage hero, bring it back in one click, or schedule it for dates
export const SavedLooks = ({ saveFirst, onApplied }) => {
  const [looks, setLooks] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState("");
  const reload = () => api.get("/admin/hero/looks").then((r) => setLooks(r.data.looks)).catch(() => setLooks([]));
  useEffect(() => { reload(); }, []);
  const add = async () => {
    setBusy("new");
    try {
      await saveFirst();
      await api.post("/admin/hero/looks", { name: name.trim() });
      toast.success(`Saved "${name.trim()}"`);
      setName("");
      reload();
    } catch (e) { err(e, "Couldn't save the look"); }
    setBusy("");
  };
  const apply = (look, editing = false) => {
    setBusy(look.id);
    api.post(`/admin/hero/looks/${look.id}/apply`).then(() => {
      toast.success(editing ? `Editing "${look.name}" — it's live; make changes below, then press "Save my changes to this look"` : `"${look.name}" is live on your homepage`);
      reload(); onApplied();
    }).catch((e) => err(e, "Couldn't apply")).finally(() => setBusy(""));
  };
  const [preview, setPreview] = useState(null);
  const [cal, setCal] = useState(false);
  const closePreview = useCallback(() => setPreview(null), []);
  const duplicate = (look) => api.post(`/admin/hero/looks/${look.id}/duplicate`).then((r) => { toast.success(`Copied as "${r.data.name}"`); reload(); }).catch((e) => err(e, "Couldn't copy"));
  const update = async (look) => {
    setBusy(look.id);
    try {
      await saveFirst();
      await api.put(`/admin/hero/looks/${look.id}/snapshot`);
      toast.success(`"${look.name}" updated`);
      reload();
    } catch (e) { err(e, "Couldn't update the look"); }
    setBusy("");
  };
  return (
    <div className="space-y-3 rounded-xl border border-cyan-400/15 p-4" data-testid="saved-looks">
      <div>
        <p className={HUD.label}>Saved looks</p>
        <p className="text-[11px] text-gray-500 mt-1">Saves everything on the homepage top: car photo or video, effects and pins, headline, lettering, buttons, and desktop + sideways-phone layout. "Use this look" puts it live right away. Scheduled looks switch on by themselves and your previous look comes back after the end date.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <input value={name} maxLength={40} onChange={(e) => setName(e.target.value)} placeholder="Name this look, e.g. Christmas 2026" className={`flex-1 min-w-[200px] h-9 rounded-lg px-3 text-sm border ${HUD.input}`} data-testid="look-name-input" />
        <button onClick={add} disabled={!name.trim() || !!busy} className={`${HUD.btn} h-9 px-4 text-xs flex items-center gap-1.5 disabled:opacity-40`} data-testid="look-save-btn">
          {busy === "new" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}Save current look
        </button>
        <button onClick={() => setCal(!cal)} className={`${HUD.ghost} h-9 px-4 text-xs flex items-center gap-1.5`} data-testid="look-calendar-btn"><CalendarDays className="w-3.5 h-3.5" />{cal ? "Hide calendar" : "Calendar"}</button>
      </div>
      {cal && looks && <LookCalendar looks={looks} />}
      {looks === null ? <Loader2 className="w-4 h-4 animate-spin text-cyan-300" /> : looks.length === 0
        ? <p className="text-xs text-gray-500" data-testid="looks-empty">No saved looks yet.</p>
        : <div className="flex flex-wrap gap-3" data-testid="looks-list">{looks.map((l) => <LookCard key={l.id} look={l} busy={busy === l.id} onApply={apply} onUpdate={update} onReload={reload} onPreview={setPreview} onDuplicate={duplicate} />)}</div>}
      {preview && <LookPreview look={preview} onClose={closePreview} onApply={apply} />}
    </div>
  );
};
